import { Center, Text3D, useTexture } from '@react-three/drei'
import { Select } from '@react-three/postprocessing'
import { createElement, useMemo, useRef, useState } from 'react'
import { MathUtils, MeshMatcapMaterial, type Group } from 'three'

import { SphereHitbox } from '@components/helpers'
import {
  WagonContainerGreenModel,
  WagonContainerRedModel,
  WagonFlatModel,
} from '@components/models'
import { isLevelComplete } from '@config'
import { useIsTouch, useVehicleKinematics } from '@hooks'
import { GamePhase, useGameStore } from '@stores'
import { RAIL_HEIGHT } from '@utils'

interface LevelSelectionConfig {
  level: number
  rotation: number
}

const LEVELS_MAP: Record<string, LevelSelectionConfig> = {
  NPC_W1: { level: 4, rotation: -90 },
  NPC_W2: { level: 3, rotation: -90 },
  NPC_W3: { level: 2, rotation: -90 },
  NPC_W4: { level: 1, rotation: -90 },

  NPC_W7: { level: 5, rotation: 90 },
  NPC_W8: { level: 6, rotation: 90 },
  NPC_W9: { level: 7, rotation: 90 },
  NPC_W10: { level: 8, rotation: 90 },

  NPC_W13: { level: 12, rotation: -90 },
  NPC_W14: { level: 11, rotation: -90 },
  NPC_W15: { level: 10, rotation: -90 },
  NPC_W16: { level: 9, rotation: -90 },
}

interface WagonProps {
  id: string
}

const BUTTON_TEXT_MATERIAL = new MeshMatcapMaterial({ color: 'white' })

export function LevelSelectionWagon({ id }: WagonProps) {
  const isTouch = useIsTouch()

  const ref = useRef<Group>(null)
  useVehicleKinematics(ref, id)

  const phase = useGameStore(state => state.phase)
  const setNextLevelIndex = useGameStore(state => state.setNextLevelIndex)
  const setLevelComplete = useGameStore(state => state.setLevelComplete)

  const isButton = LEVELS_MAP[id] !== undefined
  const [highlight, setHighlight] = useState(false)
  const autoHighlight = isButton && isTouch && phase < GamePhase.LEVEL_SELECTION_TRANSITION

  const Model = useMemo(
    () =>
      LEVELS_MAP[id]
        ? LEVELS_MAP[id].level !== 12 && isLevelComplete(LEVELS_MAP[id].level + 1)
          ? WagonContainerGreenModel
          : WagonContainerRedModel
        : WagonFlatModel,
    [id],
  )

  return (
    <group ref={ref}>
      {isButton && (
        <SphereHitbox
          position-y={RAIL_HEIGHT + 0.31}
          radius={0.5}
          onPointerOver={() => setHighlight(true)}
          onPointerOut={() => setHighlight(false)}
          onClick={event => {
            event.stopPropagation()
            setHighlight(false)

            if (phase >= GamePhase.LEVEL_SELECTION_TRANSITION) return
            setNextLevelIndex(LEVELS_MAP[id].level + 1, true)
            setLevelComplete(true)
          }}
        />
      )}

      <group position-y={RAIL_HEIGHT + 0.03}>
        {createElement(Model, {
          scale: [0.4, 0.35, 0.3],
          highlight: (highlight || autoHighlight) && phase < GamePhase.LEVEL_SELECTION_TRANSITION,
        })}
      </group>

      <Select
        enabled={(highlight || autoHighlight) && phase < GamePhase.LEVEL_SELECTION_TRANSITION}
      >
        <Center position-y={0.6}>
          <Text3D
            receiveShadow
            scale={0.5}
            scale-y={0.5}
            curveSegments={1}
            font="/fonts/jaro.json"
            material={BUTTON_TEXT_MATERIAL}
            rotation-x={MathUtils.degToRad(-90)}
            rotation-z={MathUtils.degToRad(LEVELS_MAP[id]?.rotation)}
          >
            {LEVELS_MAP[id]?.level}
          </Text3D>
        </Center>
      </Select>
    </group>
  )
}

useTexture.preload('/textures/matcap.png')
